<template>
  <!--
    embedded = ถูกวางอยู่ในการ์ดของคนอื่น (QaAssistPanel) — จึงไม่มีกรอบการ์ด/หัวของตัวเอง
    เนื้อข้างในชุดเดียวกันทั้งสองแบบ — คำแนะนำจริงมีที่เดียวในโปรเจคคือที่นี่
  -->
  <component :is="embedded ? 'div' : 'v-card'" :variant="embedded ? undefined : 'flat'" :class="embedded ? '' : 'qa-card'">
    <template v-if="!embedded">
      <v-card-title class="d-flex align-center py-3 flex-wrap">
        <v-icon color="primary" size="18" class="mr-2">mdi-lightbulb-on-outline</v-icon>
        <span class="text-subtitle-1">คำตอบที่แนะนำ</span>
        <v-chip size="x-small" color="success" variant="tonal" label class="ml-2">ข้อมูลจริง</v-chip>
        <v-spacer />
        <!--
          ⚠️ ห้ามเอา turnCount / everyNTurns กลับมาโชว์ตรงนี้ ด้วยเหตุผลเดียวกับ
             กล่องว่างข้างล่าง — ตัวนับเป็นของ**ลูกค้า 1 คน** (ทุกเคสรวมกัน) ส่วนแผงนี้
             เป็นของ**เคสใบเดียว** · เคสที่เพิ่งเปิดจะขึ้นว่า "คุยไปแล้ว 28 ช่วง"
             ทั้งที่ในเคสนี้เพิ่งเริ่มคุย อ่านแล้วเหมือนระบบกลืนคำแนะนำไป
        -->
        <span v-if="data.isEnded" class="text-caption text-medium-emphasis">
          จบการสนทนาแล้ว
        </span>
      </v-card-title>
      <v-divider />
    </template>

    <div :class="embedded ? '' : 'pa-4'">
      <!--
        ยังไม่ถึงรอบแรก — ต้องแยกจาก "ไม่มีคำแนะนำ" ให้ชัด ไม่งั้นดูเหมือนของพัง

        ⚠️ ห้ามเอา turnCount มาโชว์ตรงนี้ · ตัวนับเป็นของ**ลูกค้า 1 คน** (ทุกเคสรวมกัน)
           ส่วนกล่องนี้เป็นของ**เคสใบเดียว** · เคสที่เพิ่งเปิดจะกลายเป็น "คุยไปแล้ว 10 ช่วง
           แต่ไม่มีคำแนะนำสักอัน" ซึ่งอ่านแล้วเหมือนระบบพัง (เจอจริง 20 ส.ค. 2026)
      -->
      <v-alert v-if="!rounds.length" type="info" variant="tonal" density="compact">
        <span v-if="data.everyNTurns" class="text-body-2">
          เคสนี้ยังไม่มีคำแนะนำ — ระบบจะเสนอให้เมื่อคุยกันครบ {{ data.everyNTurns }} ช่วงในเคสนี้
          (1 ช่วง = ลูกค้าถามมา แล้วเจ้าหน้าที่ตอบกลับ 1 ครั้ง)
        </span>
        <span v-else class="text-body-2">เคสนี้ยังไม่มีคำแนะนำจากระบบ</span>
      </v-alert>

      <!--
        กล่องนี้สูงพอเห็นราว ๆ 2 คำแนะนำ แล้วเลื่อนดูที่เหลือ — เปิดไว้ตลอด ไม่ใช่เฉพาะตอนมีหลายรอบ
        เคสที่คุยกันยาวมีได้หลายสิบรอบ ถ้าปล่อยยาวไปเรื่อย ๆ ส่วนที่อยู่ถัดจากแผงนี้
        (สรุปการสนทนา / sentiment) จะถูกดันหายไปท้ายหน้าจนไม่มีใครเลื่อนไปดู
      -->
      <div class="qa-rounds-scroll">
      <div v-for="round in rounds" :key="round.roundId" class="qa-round">
        <!--
          ⚠️ ไม่แสดงหัวรอบ (รอบที่ / ช่วงคำถาม / สถานะ / เวลา / เวอร์ชันค่าตั้ง) โดยตั้งใจ
             เจ้าหน้าที่ที่กำลังคุยกับลูกค้าอยากเห็น "คำตอบ" ไม่ใช่กลไกเบื้องหลัง
             ข้อมูลพวกนั้นยังอยู่ครบใน API และไฟล์ log สำหรับตอนไล่ปัญหา

          รอบที่ไม่สำเร็จยังต้องบอกเหตุผลด้วยภาษาคน ไม่ใช่หายเงียบ —
          ไม่งั้นช่องว่างจะดูเหมือนระบบพัง ทั้งที่รู้อยู่ว่าเกิดอะไรขึ้น
        -->
        <v-alert
          v-if="round.status !== 'succeeded'"
          :type="round.status === 'failed' ? 'error' : 'info'"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          <div class="d-flex align-center" style="gap: 8px">
            <!-- ⚠️ วงหมุนเฉพาะตอน running — สถานะอื่นนิ่งอยู่แล้ว ถ้าใส่ให้ทุกสถานะ
                 คนอ่านจะเข้าใจว่าระบบยังทำงานอยู่ทั้งที่รอบนั้นจบไปแล้ว -->
            <v-progress-circular
              v-if="round.status === 'running'"
              indeterminate
              size="15"
              width="2"
              color="primary"
            />
            <span class="text-body-2">{{ statusMeta(round.status).detail(round) }}</span>
          </div>
        </v-alert>

        <template v-else>
          <v-card v-for="rec in round.recommendations" :key="rec.suggestionId" variant="outlined" class="pa-2 mb-2 qa-card-sm">
            <!--
              ⚠️ ลำดับบนการ์ดตั้งใจให้ "ประโยคที่ส่งให้ลูกค้า" อยู่บนสุดเหมือนเดิมทุกประการ
                 เจ้าหน้าที่ที่กำลังคุยอยู่ต้องการก๊อบประโยคไปตอบ ไม่ใช่มาอ่านเหตุผลก่อน
                 2 บรรทัดใหม่จึงอยู่ **ใต้** ประโยค และหายไปทั้งบรรทัดเมื่อไม่มีข้อมูล
                 (ใบที่บันทึกไว้ก่อน 25 ส.ค. 2026 หน้าตาเหมือนเดิมเป๊ะ)
            -->
            <!-- ตัดที่ 3 บรรทัด — คำตอบบางข้อยาวเป็นย่อหน้า ถ้าปล่อยเต็มจะเห็นได้ทีละข้อเดียว -->
            <div class="text-body-2 qa-rec-text" :class="{ 'qa-rec-clamp': !expanded[rec.suggestionId] }">
              {{ rec.actionText }}
            </div>
            <button v-if="isLong(rec)" type="button" class="qa-rec-more" @click="toggleText(rec)">
              {{ expanded[rec.suggestionId] ? 'ย่อ' : 'ดูเพิ่ม' }}
            </button>

            <!--
              คำแปลไทยของประโยคข้างบน — โผล่เฉพาะตอนลูกค้าไม่ได้พิมพ์ไทย

              ⚠️ **ประโยคบนคือของจริงที่ส่งให้ลูกค้า บรรทัดนี้มีไว้ให้เจ้าหน้าที่อ่านเข้าใจเท่านั้น**
                 ห้ามสลับที่กัน และห้ามเอาบรรทัดนี้ไปให้ก๊อบแทน — ลูกค้าที่พิมพ์อังกฤษ
                 จะได้รับข้อความภาษาไทยกลับไป
            -->
            <div v-if="rec.actionTextTh" class="qa-rec-th d-flex mt-1">
              <v-icon size="13" class="mr-1 mt-1 flex-shrink-0">mdi-translate</v-icon>
              <span class="text-body-2">{{ rec.actionTextTh }}</span>
            </div>

            <!--
              สิ่งที่ควรทำ + เหตุผล — ช่องเดียวกับที่ Salesforce เห็นในการ์ดคำแนะนำ
              (agentRecommendation / reason ของ /api/panel/v1/recommendations)
              ใบเก่าไม่มีค่านี้ = ไม่มีบรรทัดนี้ ไม่ใช่บรรทัดว่าง
            -->
            <div v-if="rec.agentActionText" class="qa-rec-meta d-flex mt-2">
              <v-icon size="13" color="primary" class="mr-1 mt-1 flex-shrink-0">mdi-checkbox-marked-circle-outline</v-icon>
              <span><span class="qa-rec-label">สิ่งที่ควรทำ</span> {{ rec.agentActionText }}</span>
            </div>
            <div v-if="rec.reasonText" class="qa-rec-meta d-flex mt-1">
              <v-icon size="13" class="mr-1 mt-1 flex-shrink-0">mdi-help-circle-outline</v-icon>
              <span><span class="qa-rec-label">เหตุผล</span> {{ rec.reasonText }}</span>
            </div>
            <!--
              ⚠️ ปุ่ม "ใช้ / ไม่ใช้" ถูกถอดออกจากหน้าจอตามที่ผู้ใช้ขอ — แต่ API
                 (recommend/suggestions/{id}/used) และคอลัมน์ UsedStatus/UsedBy/UsedAt
                 ยังอยู่ครบ ถ้าจะเอากลับมาให้เติมแค่ปุ่มตรงนี้ ไม่ต้องแก้ฝั่งหลังบ้าน
            -->
            <div class="d-flex align-center mt-1">
              <v-chip size="x-small" color="success" variant="tonal" label>เจ้าหน้าที่ใช้</v-chip>
            </div>
          </v-card>
        </template>
      </div>
      </div>

    </div>

  </component>
</template>

<script>
/**
 * คำตอบที่แนะนำจากงานเบื้องหลัง — แสดงแยกตามรอบ (phase)
 *
 * ⚠️ คนละตัวกับ QaAssistPanel ซึ่งเป็นข้อมูลจำลองของ prototype
 *    ตัวนั้นแสดง "สรุปการสนทนา 5 ช่อง + คำแนะนำ" แบบไม่มีรอบ ส่วนตัวนี้เป็นของจริง
 *    ที่มีรอบ มีสถานะ และเขียนกลับได้ · ไม่รวมกันเพื่อไม่ให้ผู้ใช้แยกไม่ออกว่าอันไหนของจริง
 *
 * แผนงาน: docs/qa-console/Plan-QA-Recomend.md §11.3
 */
const STATUS = {
  succeeded: { label: 'สำเร็จ', color: 'success', icon: 'mdi-check-circle-outline', detail: () => '' },
  queued: {
    label: 'รอคิว',
    color: 'grey',
    icon: 'mdi-clock-outline',
    detail: () => 'อยู่ในคิว รอถึงคิวของบทสนทนานี้',
  },
  running: {
    label: 'กำลังทำ',
    color: 'primary',
    icon: 'mdi-progress-clock',
    detail: () => 'กำลังส่งให้ AI วิเคราะห์',
  },
  failed: {
    label: 'ไม่สำเร็จ',
    color: 'error',
    icon: 'mdi-alert-circle-outline',
    detail: (r) => {
      const map = {
        'llm-unavailable': 'เรียก AI ไม่สำเร็จหรือหมดเวลา — รอบนี้ไม่ถูกลองใหม่ตามนโยบายของระบบ',
        'bad-response': 'AI ตอบมาในรูปแบบที่ระบบแกะไม่ได้ — ตรวจว่า system prompt ยังสั่งให้ตอบเป็น JSON อยู่',
        'orphaned-restart': 'ระบบถูกรีสตาร์ทระหว่างที่รอบนี้กำลังทำงาน จึงปิดทิ้งเพื่อไม่ให้เสียค่าเรียก AI ซ้ำ',
      };
      return map[r.errorCode] || `ไม่สำเร็จ (${r.errorCode || 'ไม่ทราบสาเหตุ'})`;
    },
  },
  cancelled_chat_ended: {
    label: 'ยกเลิก',
    color: 'grey',
    icon: 'mdi-cancel',
    detail: () => 'จบการสนทนาก่อนที่รอบนี้จะถึงคิว จึงถูกยกเลิกตามค่าตั้งของระบบ',
  },
  rejected_queue_full: {
    label: 'คิวเต็ม',
    color: 'warning',
    icon: 'mdi-tray-full',
    detail: () => 'คิวเต็มตอนที่รอบนี้เกิดขึ้น จึงไม่ได้ถูกส่งเข้า AI',
  },
  skipped_cap: {
    label: 'ชนเพดาน',
    color: 'warning',
    icon: 'mdi-speedometer-slow',
    detail: () => 'ชนเพดานจำนวนรอบที่ตั้งไว้ (ต่อบทสนทนา หรือต่อวัน) จึงข้ามรอบนี้',
  },
};

export default {
  name: 'QaRecommendPanel',
  props: {
    data: { type: Object, required: true },
    /** true = เรนเดอร์เฉพาะเนื้อใน ไม่ห่อการ์ดซ้อนการ์ด */
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      /** suggestionId ที่ผู้ใช้กดขยายอ่านเต็ม */
      expanded: {},
    };
  },
  computed: {
    /**
     * รอบทั้งหมดของเคสนี้ — อ่านผ่านตัวนี้เสมอ ห้ามแตะ `data.rounds` ตรง ๆ ในเทมเพลต
     *
     * ⚠️ prop เป็น `type: Object` ก็จริง แต่ Vue เช็คชนิดแค่เตือนใน console ไม่ได้กันจริง
     *    ของที่ไม่ใช่รูปนี้เคยหลุดเข้ามาแล้ว (API ตอบ HTML ของ SPA เพราะ route ไม่มีบน
     *    build ที่ deploy อยู่) แล้ว `data.rounds.length` กลายเป็น TypeError ตอน render
     *    ซึ่งพาการ์ดผู้ช่วย AI และแผง sentiment ที่อยู่ถัดไปหายไปทั้งคู่
     *
     *    ต้นตอถูกปิดที่ `qaRecommendApi.js` แล้ว (โยน error แทนคืน HTML) — ตัวนี้เป็นตาข่าย
     *    ชั้นสอง เพื่อให้ "ข้อมูลผิดรูป" จบลงที่กล่อง "ยังไม่มีคำแนะนำ" ไม่ใช่หน้าพังทั้งหน้า
     */
    rounds() {
      const list = this.data && this.data.rounds;
      return Array.isArray(list) ? list : [];
    },
  },
  methods: {
    /** ยาวพอจะโดนตัด 3 บรรทัดไหม — ประมาณจากจำนวนตัวอักษร ไม่ต้องวัดความสูงจริง */
    isLong(rec) {
      return (rec.actionText || '').length > 120;
    },
    toggleText(rec) {
      this.expanded = { ...this.expanded, [rec.suggestionId]: !this.expanded[rec.suggestionId] };
    },
    statusMeta(status) {
      return STATUS[status] || { label: status, color: 'grey', icon: 'mdi-help-circle-outline', detail: () => status };
    },
  },
};
</script>

<style scoped>
.qa-rounds-scroll {
  /* สูงพอเห็นราว ๆ 2 คำแนะนำ แล้วเลื่อนดูที่เหลือ — เปิดไว้ตลอด ไม่ใช่เฉพาะตอนมีหลายรอบ */
  max-height: 300px;
  overflow-y: auto;
  padding-right: 6px;
}
.qa-rounds-scroll::-webkit-scrollbar {
  width: 6px;
}
.qa-rounds-scroll::-webkit-scrollbar-thumb {
  background: #d5dae0;
  border-radius: 3px;
}
.qa-rec-text {
  line-height: 1.45;
}
.qa-rec-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.qa-rec-th {
  /* ⚠️ จงใจให้จางกว่าประโยคหลัก — คนต้องแยกออกด้วยตาว่าอันไหนคือข้อความที่ส่งจริง */
  color: rgba(0, 0, 0, 0.6);
  border-left: 2px solid #e3e6ea;
  padding-left: 6px;
  line-height: 1.45;
}
.qa-rec-meta {
  font-size: 12px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.68);
}
.qa-rec-label {
  color: rgba(0, 0, 0, 0.45);
  margin-right: 4px;
}
.qa-rec-more {
  margin-top: 2px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 11px;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
}
.qa-round {
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f5;
}
.qa-round:last-of-type {
  border-bottom: none;
  padding-bottom: 0;
}
</style>
